// FILE: /src/Components/PostUploader/imagegenerator.js (FULLY UPDATED)

import React, { useState } from "react";
import { Modal, Spin, message, Input, Select, Button, Divider, Typography, Tooltip, Image as AntImage } from "antd";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { generateImageService } from "../../services/ai";
import UpgradeModal from "../BuySubscription/UpgradeModal";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const AiImageGenerator = ({ isOpen, onClose, onImagesGenerated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generatedImages, setGeneratedImages] = useState([]);
  
  const [model, setModel] = useState("Oolook V1");
  const [numImages, setNumImages] = useState(1);
  const [upgradeModal, setUpgradeModal] = useState({ visible: false, message: '', featureName: '' });

  const { selectedGroup } = useSelector((state) => state.groups);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      message.warning("Please enter a prompt to generate images.");
      return;
    }
    if (!selectedGroup) {
      message.error("Please select a brand/group before generating an image.");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await generateImageService({
        prompt,
        n: numImages,
        expand_prompt: model === "Oolook V2",
        group_name: selectedGroup,
      });

      const newImages = response || [];
      if (newImages.length > 0) {
        setGeneratedImages(prev => [...prev, ...newImages]);
        message.success(`${newImages.length} new image(s) generated!`);
      } else {
        message.error("The AI could not generate images for this prompt. Please try a different one.");
      }
    } catch (error) {
      if (error.isLimitError) {
        setUpgradeModal({ visible: true, message: error.message, featureName: error.feature });
      } else {
        console.error("Image generation error:", error);
        message.error("Failed to generate images. The service may be temporarily unavailable.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsert = (imageUrl) => {
    if (typeof onImagesGenerated === 'function') {
      onImagesGenerated([imageUrl]);
      message.success("Image inserted into your post!");
    }
  };

  const handleCloseModal = () => {
    setGeneratedImages([]);
    setPrompt("");
    setModel("Oolook V1");
    setNumImages(1);
    onClose();
  };
  
  return (
    <>
      <Modal
        title={<Title level={4}>Generate Image</Title>}
        open={isOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div className="space-y-6 p-2">
          {/* --- Input Section --- */}
          <div className="space-y-4">
            <div>
              <label className="font-semibold text-gray-700">Prompt</label>
              <TextArea
                rows={4}
                placeholder="e.g., A futuristic city skyline at sunset, digital art"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                  <label className="font-semibold text-gray-700">Model:</label>
                  <Select value={model} onChange={setModel} style={{ width: 120 }}>
                      <Option value="Oolook V1">Oolook V1</Option>
                      <Option value="Oolook V2">Oolook V2</Option>
                  </Select>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-semibold text-gray-700">Number of Images:</label>
                <Select value={numImages} onChange={setNumImages} style={{ width: 70 }}>
                    <Option value={1}>1</Option>
                    <Option value={2}>2</Option>
                    <Option value={3}>3</Option>
                </Select>
              </div>
            </div>
            <Button type="primary" block onClick={handleGenerate} loading={isLoading} size="large">
              {isLoading ? 'Generating...' : (generatedImages.length > 0 ? 'Generate More' : 'Generate Images')}
            </Button>
          </div>

          {/* --- Output Section --- */}
          {generatedImages.length > 0 && (
            <>
              <Divider />
              <div className="space-y-4">
                <Title level={5}>Generated Images ({generatedImages.length})</Title>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-[40vh] overflow-y-auto pr-2">
                  {generatedImages.map((imageUrl, index) => (
                    <div key={index} className="space-y-2">
                      <div className="relative aspect-square overflow-hidden rounded-lg shadow-md border">
                        <AntImage
                          width="100%"
                          height="100%"
                          src={imageUrl}
                          alt={`Generated image ${index + 1}`}
                          className="object-cover"
                          preview={{ mask: <i className="fa-solid fa-eye text-white text-lg"></i> }}
                        />
                      </div>
                      <div className="flex justify-center gap-2">
                        <Tooltip title="Download">
                          <a href={imageUrl} download={`generated_image_${index + 1}.png`} target="_blank" rel="noopener noreferrer">
                            <Button icon={<DownloadOutlined />}>Download</Button>
                          </a>
                        </Tooltip>
                        <Tooltip title="Insert into post">
                          <Button icon={<PlusOutlined />} onClick={() => handleInsert(imageUrl)}>Insert</Button>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          {isLoading && 
            <div className="text-center py-4">
              <Spin size="default" />
              <p className="mt-2 text-gray-500">Generating new images...</p>
            </div>
          }
        </div>
      </Modal>

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() => setUpgradeModal({ visible: false, message: '', featureName: '' })}
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />
    </>
  );
};

export default AiImageGenerator;